// src/pages/Menu.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const PageContainer = styled.div`
  max-width: 1000px;
  margin: 40px auto;
  padding: 0 20px;
`;

/* ---------- NAV (desktop + mobile) ---------- */
const NavWrap = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

/* Desktop tabs */
const TabBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 14px;

  @media (max-width: 860px) {
    display: none;
  }
`;

const Tab = styled.button`
  background: transparent;
  border: none;
  font-size: 1rem;
  color: #e1d6c0;
  padding: 10px 15px;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  &:hover { color: #ffffff; }
  &.active { border-color: #e1d6c0; color: #ffffff; }
`;

/* Mobile bar + dropdown */
const MobileBar = styled.div`
  display: none;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  @media (max-width: 860px) {
    display: flex;
  }
`;

const BrandLink = styled(Link)`
  color: #e1d6c0;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.05rem;
`;

const MenuToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  color: #e1d6c0;
  border: 1px solid #e1d6c0;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
`;

const DropMenu = styled.div`
  position: absolute;
  top: 42px;
  left: 0;
  right: 0;
  background: rgba(13,13,13,0.95);
  border: 1px solid #333;
  border-radius: 12px;
  padding: 8px;
  z-index: 10;
  display: ${p => (p.open ? 'block' : 'none')};

  @media (min-width: 861px) {
    display: none;
  }
`;

const DropItem = styled.button`
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  color: #e1d6c0;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.06); color: #fff; }
`;

const DropLink = styled(Link)`
  display: block;
  color: #e1d6c0;
  padding: 10px 12px;
  border-radius: 8px;
  text-decoration: none;
  &:hover { background: rgba(255,255,255,0.06); color: #fff; }
`;

/* ---------- LIST / GRID ---------- */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

const Card = styled.div`
  border: 1px solid #e1d6c0;
  padding: 20px;
  border-radius: 8px;
  background: rgba(13,13,13,0.8);
`;

const CategoryHeader = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 15px;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-bottom: 1px solid #e1d6c0;
  padding-bottom: 5px;
  color: #e1d6c0;
`;

const ItemList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ItemRow = styled.li`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px dashed #444;
  &:last-child { border-bottom: none; }
`;

const ItemName = styled.span`
  color: #ffffff;
`;

const ItemPrice = styled.span`
  color: #e1d6c0;
`;

const AddButton = styled.button`
  margin-left: 12px;
  background: #e1d6c0;
  color: #0d0d0d;
  border: none;
  border-radius: 16px;
  font-weight: 700;
  padding: 4px 10px;
  cursor: pointer;
  &:hover { opacity: 0.85; }
`;

const AddedNote = styled.span`
  color: #7CFC00;
  font-size: 0.9rem;
  margin-left: 8px;
`;

/* ---------- helpers ---------- */
const formatPrice = (p) => {
  if (typeof p === 'number') return `$${p.toFixed(2)}`;
  const cleaned = String(p).trim();
  if (/^\$/i.test(cleaned)) return cleaned;
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? `$${num.toFixed(2)}` : cleaned;
};

// case-insensitive match for “Drinks” page title
const isDrinksPage = (title) => /drink/i.test(title || '');

/* ---------- component ---------- */
export default function Menu() {
  const [menuData, setMenuData] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageIndex, setPageIndex] = useState(0);
  const { addItem } = useCart();
  const [addedMap, setAddedMap] = useState({});

  // mobile menu state
  const [open, setOpen] = useState(false);

  const onAdd = (name, price) => {
    addItem(name, price);
    setAddedMap((m) => ({ ...m, [name]: true }));
    setTimeout(() => setAddedMap((m) => ({ ...m, [name]: false })), 900);
  };

  useEffect(() => {
    Promise.all([
      fetch('/api/pages').then(r => r.json()),
      fetch('/api/menu').then(r => r.json())
    ]).then(([pagesData, menuData]) => {
      setPages(pagesData);
      setMenuData(menuData);
      setLoading(false);
    });
  }, []);

  // close dropdown on resize up to desktop
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 860) setOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (loading) return <PageContainer>Loading…</PageContainer>;

  const current = pages[pageIndex];
  const visibleData = menuData.filter(cat =>
    current?.categories?.includes(cat.category)
  );

  const hideAddButtons = isDrinksPage(current?.title);

  const pickPage = (idx) => {
    setPageIndex(idx);
    setOpen(false);
  };

  return (
    <PageContainer>
      <NavWrap>
        {/* Desktop tabs */}
        <TabBar>
          <Tab as={Link} to="/">Home</Tab>
          {pages.map((p, idx) => (
            <Tab
              key={p.id}
              onClick={() => setPageIndex(idx)}
              className={idx === pageIndex ? 'active' : ''}
            >
              {p.title}
            </Tab>
          ))}
        </TabBar>

        {/* Mobile hamburger */}
        <MobileBar>
          <BrandLink to="/" onClick={() => setOpen(false)}>Home</BrandLink>
          <MenuToggle
            aria-label="Toggle menu"
            aria-expanded={open}
            onClick={() => setOpen(v => !v)}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Menu
          </MenuToggle>
          <DropMenu open={open} onClick={() => setOpen(false)}>
            <DropLink to="/">Home</DropLink>
            {pages.map((p, idx) => (
              <DropItem key={p.id} onClick={(e) => { e.stopPropagation(); pickPage(idx); }}>
                {idx === pageIndex ? '• ' : ''}{p.title}
              </DropItem>
            ))}
          </DropMenu>
        </MobileBar>
      </NavWrap>

      <Grid>
        {visibleData.map(cat => (
          <Card key={cat.category}>
            <CategoryHeader>{cat.category}</CategoryHeader>
            <ItemList>
              {cat.items.map(item => (
                <ItemRow key={item.name}>
                  <ItemName>{item.name}</ItemName>
                  <div>
                    <ItemPrice>{formatPrice(item.price)}</ItemPrice>

                    {/* Hide Add / Added on Drinks page */}
                    {!hideAddButtons && (
                      <>
                        <AddButton onClick={() => onAdd(item.name, item.price)}>Add</AddButton>
                        {addedMap[item.name] ? <AddedNote>Added</AddedNote> : null}
                      </>
                    )}
                  </div>
                </ItemRow>
              ))}
            </ItemList>
          </Card>
        ))}
      </Grid>
    </PageContainer>
  );
}
